/**
 * Service Worker Registration for MicroTourney PWA
 * 
 * Enhanced service worker registration with update management,
 * offline detection, and user-friendly update prompts.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

// Configuration for service worker behavior
const SW_CONFIG = {
  // Enable automatic updates in production
  autoUpdate: process.env.NODE_ENV === 'production',
  // Check for updates every 30 minutes
  updateCheckInterval: 30 * 60 * 1000,
  // Show update prompt after delay
  updatePromptDelay: 2000,
  // Enable background sync for offline actions
  enableBackgroundSync: true,
  // Cache strategy configuration
  cacheStrategy: {
    apiCalls: 'NetworkFirst',
    assets: 'CacheFirst',
    images: 'CacheFirst'
  }
};

// Global state for service worker
let swRegistration = null;
let isUpdateAvailable = false;
let updatePromptShown = false;

/**
 * Service worker update event listeners
 */
const swUpdateListeners = new Set();

/**
 * Add listener for service worker updates
 * @param {Function} listener - Callback function
 */
export function addSWUpdateListener(listener) {
  swUpdateListeners.add(listener);
}

/**
 * Remove listener for service worker updates
 * @param {Function} listener - Callback function
 */
export function removeSWUpdateListener(listener) {
  swUpdateListeners.delete(listener);
}

/**
 * Notify all listeners about service worker events
 * @param {string} type - Event type
 * @param {Object} data - Event data
 */
function notifyListeners(type, data = {}) {
  swUpdateListeners.forEach(listener => {
    try {
      listener({ type, ...data });
    } catch (error) {
      console.error('SW listener error:', error);
    }
  });
}

/**
 * Check if a new service worker version is available
 */
function checkForUpdates() {
  if (swRegistration) {
    console.log('🔄 Checking for service worker updates...');
    swRegistration.update().catch(error => {
      console.error('❌ SW update check failed:', error);
    });
  }
}

/**
 * Skip waiting and activate new service worker immediately
 */
export function skipWaitingAndReload() {
  if (swRegistration && swRegistration.waiting) {
    console.log('⏭️ Skipping waiting and activating new SW...');
    
    // Send skip waiting message
    swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload the page to activate new SW
    window.location.reload();
  }
}

/**
 * Get current service worker status
 */
export function getServiceWorkerStatus() {
  return {
    isSupported: 'serviceWorker' in navigator,
    isRegistered: swRegistration !== null,
    isUpdateAvailable,
    isOnline: navigator.onLine,
    registration: swRegistration
  };
}

/**
 * Send message to service worker
 * @param {Object} message - Message object
 */
export function sendSWMessage(message) {
  if (swRegistration && swRegistration.active) {
    swRegistration.active.postMessage(message);
  }
}

/**
 * Register service worker with enhanced features
 * @param {Object} config - Configuration options
 */
export function register(config = {}) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    
    if (publicUrl.origin !== window.location.origin) {
      console.warn('⚠️ SW registration skipped: different origins');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        
        navigator.serviceWorker.ready.then(() => {
          console.log('👨‍💻 SW ready in localhost mode');
        });
      } else {
        registerValidSW(swUrl, config);
      }
      
      // Set up periodic update checks
      if (SW_CONFIG.autoUpdate) {
        setInterval(checkForUpdates, SW_CONFIG.updateCheckInterval);
      }
    });
  } else {
    console.log('❌ Service workers not supported');
    notifyListeners('unsupported');
  }
}

/**
 * Register valid service worker
 */
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      console.log('✅ SW registered successfully');
      swRegistration = registration;
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;

        if (installingWorker === null) return;
        
        console.log('🔄 New SW version found, installing...');
        notifyListeners('installing');
        
        installingWorker.addEventListener('statechange', () => {
          switch (installingWorker.state) {
            case 'installed':
              if (navigator.serviceWorker.controller) {
                console.log('✨ New SW version available');
                isUpdateAvailable = true;
                
                // Show update prompt after delay
                setTimeout(() => {
                  if (!updatePromptShown) {
                    notifyListeners('updateAvailable', { 
                      registration,
                      skipWaiting: skipWaitingAndReload 
                    });
                    updatePromptShown = true;
                  }
                }, SW_CONFIG.updatePromptDelay);
              } else {
                console.log('✅ SW cached for offline use');
                notifyListeners('cached');
              }
              break;
              
            case 'redundant':
              console.log('❌ SW installation failed');
              notifyListeners('error', { error: 'Installation failed' });
              break;
              
            default:
              console.log(`🔄 SW state: ${installingWorker.state}`);
          }
        });
      });
      
      // Listen for SW messages
      navigator.serviceWorker.addEventListener('message', event => {
        const { type, payload } = event.data || {};
        
        switch (type) {
          case 'SW_UPDATED':
            console.log('🔄 SW updated successfully');
            notifyListeners('updated');
            break;
            
          case 'CACHE_UPDATED':
            console.log('📦 Cache updated');
            notifyListeners('cacheUpdated', payload);
            break;
            
          case 'OFFLINE_BACKUP':
            console.log('💾 Offline backup created');
            notifyListeners('offlineBackup', payload);
            break;
            
          case 'SYNC_COMPLETE':
            console.log('🔄 Background sync completed');
            notifyListeners('syncComplete', payload);
            break;
            
          default:
            console.log('📨 SW message:', event.data);
        }
      });
      
      // Handle controller change (new SW activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 SW controller changed');
        notifyListeners('controllerChange');
        
        // Reload page if auto-update is enabled
        if (SW_CONFIG.autoUpdate && isUpdateAvailable) {
          window.location.reload();
        }
      });
      
      if (config && config.onSuccess) {
        config.onSuccess(registration);
      }
    })
    .catch(error => {
      console.error('❌ SW registration failed:', error);
      notifyListeners('error', { error });
      
      if (config && config.onError) {
        config.onError(error);
      }
    });
}

/**
 * Check if service worker is valid (for localhost)
 */
function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      const contentType = response.headers.get('content-type');
      
      if (
        response.status === 404 ||
        (contentType !== null && contentType.indexOf('javascript') === -1)
      ) {
        console.warn('⚠️ SW not found or invalid');
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('🌐 App running offline, SW not available');
      notifyListeners('offline');
    });
}

/**
 * Unregister service worker
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        console.log('🗑️ Unregistering SW...');
        return registration.unregister();
      })
      .then(() => {
        console.log('✅ SW unregistered');
        swRegistration = null;
        notifyListeners('unregistered');
      })
      .catch(error => {
        console.error('❌ SW unregistration failed:', error);
      });
  }
}

/**
 * Clear all caches (useful for debugging)
 */
export function clearCaches() {
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      return Promise.all(
        cacheNames.map(cacheName => {
          console.log(`🗑️ Deleting cache: ${cacheName}`);
          return caches.delete(cacheName);
        })
      );
    }).then(() => {
      console.log('✅ All caches cleared');
      notifyListeners('cachesCleared');
    });
  }
}

/**
 * Get cache storage usage
 */
export async function getCacheUsage() {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage,
        total: estimate.quota,
        usedMB: Math.round((estimate.usage || 0) / 1024 / 1024),
        totalMB: Math.round((estimate.quota || 0) / 1024 / 1024),
        percentage: estimate.quota ? Math.round((estimate.usage || 0) / estimate.quota * 100) : 0
      };
    } catch (error) {
      console.error('❌ Failed to get storage estimate:', error);
      return null;
    }
  }
  return null;
}

/**
 * Request persistent storage (to prevent cache eviction)
 */
export async function requestPersistentStorage() {
  if ('storage' in navigator && 'persist' in navigator.storage) {
    try {
      const isPersistent = await navigator.storage.persist();
      console.log(`📦 Persistent storage: ${isPersistent ? 'granted' : 'denied'}`);
      return isPersistent;
    } catch (error) {
      console.error('❌ Failed to request persistent storage:', error);
      return false;
    }
  }
  return false;
}

// Export configuration for use in other modules
export { SW_CONFIG };
